import { css } from '@emotion/react';
import { ReactNode, forwardRef } from 'react';
import { useFormContext } from 'react-hook-form';

import { colors } from '~src/theme/colors';
import { fontStyles } from '~src/theme/fontStyles';
import { mediaQueries } from '~src/theme/mediaQueries';
import { getBorderColor, getLabelColor } from '~src/ui/common-styles';
import { InputError } from '~src/ui/input-error';

import { AddressFormFields } from '../../types';
import { StandardInputProps } from '../standard-input';

export type PhoneNumberInputBaseProps = StandardInputProps & {
  inputExtensionElement?: ReactNode;
  rightElement?: ReactNode;
  onFocus: () => void;
  onBlur: () => void;
  focused?: boolean;
  countryCallingCode?: string;
};

/**
 * NOTE: this component is used as a base form phone-number-input
 * you probably want to use the other one
 */

export const PhoneNumberInputBase = forwardRef<HTMLInputElement, PhoneNumberInputBaseProps>(
  (
    {
      name,
      label,
      inputExtensionElement,
      rightElement,
      onFocus,
      onBlur,
      focused,
      warning,
      countryCallingCode,
      ...props
    },
    ref
  ) => {
    const {
      getValues,
      formState: { errors },
    } = useFormContext<AddressFormFields>();

    const isLabelRaised = !!inputExtensionElement || focused || !!getValues(name);
    const hasError = !!errors[name];

    const getInputPadding = (phonePrefix?: string) => {
      switch (phonePrefix?.length) {
        case 1: // For example, US (+1)
          return '55px'; // Less padding
        case 2: // For example, Poland (+48)
          return '66px'; // Medium padding
        case 3: // For example, Slovakia (+386)
          return '75px'; // More padding
        default:
          return '75px'; // Default padding
      }
    };

    return (
      <>
        <div css={styles.phoneNumberField(focused, hasError)}>
          <div css={styles.phoneNumberWrapper} onFocus={onFocus} onBlur={onBlur}>
            <label
              htmlFor={name}
              css={styles.label(isLabelRaised, hasError, warning)}
              aria-hidden={errors ? 'false' : 'true'}
            >
              {label}
            </label>
            <div css={styles.inputWrapper}>
              {inputExtensionElement}
              <input
                ref={e => {
                  if (typeof ref === 'function') {
                    ref(e);
                  } else if (!!ref) {
                    ref.current = e;
                  }
                }}
                css={styles.input(getInputPadding(countryCallingCode))}
                data-test="phone_number"
                {...props}
              />
            </div>
            {rightElement && <div css={styles.extensionStyles}>{rightElement}</div>}
          </div>
        </div>
        {hasError && <InputError dataTest="phone_number-error">{errors[name]?.message}</InputError>}
      </>
    );
  }
);

const styles = {
  phoneNumberWrapper: css({
    position: 'relative',
    display: 'flex',
    width: '100%',
  }),
  phoneNumberField: (focused?: boolean, error?: boolean) =>
    css({
      position: 'relative',
      display: 'flex',
      height: '60px',
      backgroundColor: 'transparent',
      borderColor: getBorderColor(!!focused, !!error),
      borderStyle: 'solid',
      borderWidth: '1px',
      borderRadius: '2px',
    }),
  inputWrapper: css({
    transition: 'all 0.2s',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  }),
  label: (raiseLabel: boolean, error?: boolean, warning?: boolean) =>
    css({
      top: raiseLabel ? '11px' : '50%',
      transform: `translate(0, ${raiseLabel ? '0' : '-50%'})`,
      left: '16px',
      right: '16px',
      position: 'absolute',
      transition: 'all .1s ease-in-out',
      pointerEvents: 'none',
      cursor: 'text',
      color: getLabelColor(!!error, warning),
      ...(raiseLabel ? fontStyles.px12_16 : fontStyles.px16_20),
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    }),
  input: (leftPadding: string) =>
    css({
      border: 'none',
      color: colors.black,
      height: '100%',
      width: '100%',
      outline: 'none',
      WebkitAppearance: 'none',
      boxSizing: 'border-box',
      padding: `30px 40px 12px ${leftPadding}`,
      ...fontStyles.px14_20,
      resize: 'none',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      [mediaQueries.S]: {
        ...fontStyles.px16_20, // disable auto-zoom on ios.
      },
    }),
  extensionStyles: css({
    position: 'absolute',
    right: '15px',
    top: '22px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  }),
};
